import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNewImage } from '../store/customizerSlice';
import { saveImageToDB } from '../utils/indexedDB';
import { useNavigate, useLocation } from 'react-router-dom';
import { CUSTOMIZER_MAX_IMAGES_COUNT } from '../config/config';
import { useAppNavigate } from './useAppNavigate';

const useFileHandlers = () => {
  const CANVAS_WIDTH = 900;
  const CANVAS_HEIGHT = 1200;
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();
  
  const customizerImages = useSelector((state) => state.customizer.images);

  const processImage = async (file, redirectToCustomizerPage, customProperties = null, customImageId = null, setUploadedFile = null) => {
    if (!file || !file.type.startsWith("image/") || file.type === "image/svg+xml") return;
  
    if (customizerImages.length >= CUSTOMIZER_MAX_IMAGES_COUNT) {
      alert(`You can only upload up to ${CUSTOMIZER_MAX_IMAGES_COUNT} images.`);
      return;
    }
  
    const MAX_CANVAS_COVERAGE = 1.3; // 130% of canvas width or height
  
    const reader = new FileReader();
    reader.readAsDataURL(file);
  
    reader.onload = async (event) => {
      const imageBlob = await fetch(event.target.result).then((res) => res.blob());
      const id = customImageId || crypto.randomUUID();
  
      const img = new Image();
      img.onload = async () => {
        if (customProperties) {
          try {
            const parsedProps = typeof customProperties === 'string'
              ? JSON.parse(customProperties)
              : customProperties;
  
            const newImage = {
              id,
              x: parsedProps.x || 0,
              y: parsedProps.y || 0,
              width: parsedProps.width || CANVAS_WIDTH * 0.5,
              height: parsedProps.height || (parsedProps.width ? parsedProps.width / (img.naturalWidth / img.naturalHeight) : CANVAS_HEIGHT * 0.5),
              rotation: parsedProps.rotation || 0,
              scale: parsedProps.scale || 1.0,
              maxScale: parsedProps.maxScale || 5,
            };
  
            await saveImageToDB("uploadedImages", id, imageBlob);
            dispatch(addNewImage(newImage));
          } catch (err) {
            console.error("Invalid customProperties JSON:", err);
            return;
          }
        } else {
          const position = { x: 0.5, y: 0.61 };
          const aspectRatio = img.naturalWidth / img.naturalHeight;
          let baseWidth, baseHeight;

          if (aspectRatio >= 1) {
            baseHeight = CANVAS_HEIGHT * 0.8;
            baseWidth = baseHeight * aspectRatio;
          } else {
            baseWidth = CANVAS_WIDTH;
            baseHeight = baseWidth / aspectRatio;
          }

          const initialScale = 1.0;
          const maxScale = Math.round(
            Math.max(
              (CANVAS_WIDTH * MAX_CANVAS_COVERAGE) / baseWidth,
              (CANVAS_HEIGHT * MAX_CANVAS_COVERAGE) / baseHeight
            ) * 10
          ) / 10;

          const newImage = {
            id,
            x: CANVAS_WIDTH * position.x - baseWidth / 2,
            y: CANVAS_HEIGHT * position.y - baseHeight / 2,
            width: baseWidth,
            height: baseHeight,
            rotation: 0,
            scale: initialScale,
            maxScale,
          };

          await saveImageToDB("uploadedImages", id, imageBlob);
          dispatch(addNewImage(newImage));
          
        }

        if (setUploadedFile) {
          const fileName = `upload-${id}.png`;
          const reconstructedFile = new File([imageBlob], fileName, { type: "image/png" });
          setUploadedFile(reconstructedFile);
        }
  
        if (redirectToCustomizerPage) {
          navigate(`/customizer${location.search}`);
        }
      };
  
      img.src = event.target.result;
    };
  };
  
  const handleFileSelect = (e, redirectToCustomizerPage = true, setUploadedFile = null) => {
    if (e.target.files.length) {
      processImage(e.target.files[0], redirectToCustomizerPage, null, null, setUploadedFile);
      e.target.value = "";
    }
  };
  
  const handleDrop = (e, redirectToCustomizerPage = true, setUploadedFile = null) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) {
      processImage(e.dataTransfer.files[0], redirectToCustomizerPage, null, null, setUploadedFile);
    }
  };
  
  return { handleFileSelect, handleDrop, processImage };
};

export default useFileHandlers;
